import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import type { Components } from 'react-markdown';

interface ChatMarkdownProps {
  content: string;
  variant?: 'user' | 'assistant';
}

export default function ChatMarkdown({ content, variant = 'assistant' }: ChatMarkdownProps) {
  const isUser = variant === 'user';

  const components: Components = {
    h1: ({ children }) => (
      <h1 className={cn(
        'text-xl font-display font-semibold mb-3 mt-4 first:mt-0',
        isUser ? 'text-white' : 'text-steel-azure-800'
      )}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className={cn(
        'text-lg font-display font-semibold mb-2 mt-3 first:mt-0',
        isUser ? 'text-white' : 'text-steel-azure-700'
      )}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className={cn(
        'text-base font-display font-medium mb-2 mt-3 first:mt-0',
        isUser ? 'text-white' : 'text-steel-azure-700'
      )}>{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className={cn(
        'text-sm font-display font-medium mb-1.5 mt-2 first:mt-0',
        isUser ? 'text-white' : 'text-steel-azure-600'
      )}>{children}</h4>
    ),
    h5: ({ children }) => (
      <h5 className={cn(
        'text-sm font-display font-medium mb-1 mt-2 first:mt-0',
        isUser ? 'text-white/90' : 'text-steel-azure-600'
      )}>{children}</h5>
    ),
    h6: ({ children }) => (
      <h6 className={cn(
        'text-xs font-display font-medium mb-1 mt-2 first:mt-0 uppercase tracking-wide',
        isUser ? 'text-white/80' : 'text-steel-azure-500'
      )}>{children}</h6>
    ),
    p: ({ children }) => (
      <p className={cn(
        'text-sm md:text-base font-light leading-relaxed mb-2 last:mb-0',
        isUser ? 'text-white' : 'text-calm-text'
      )}>{children}</p>
    ),
    strong: ({ children }) => (
      <strong className={cn(
        'font-semibold',
        isUser ? 'text-white' : 'text-steel-azure-800'
      )}>{children}</strong>
    ),
    em: ({ children }) => (
      <em className={cn(
        'italic',
        isUser ? 'text-white/90' : 'text-calm-text'
      )}>{children}</em>
    ),
    ul: ({ children }) => (
      <ul className={cn(
        'list-disc pl-5 mb-2 space-y-1',
        isUser ? 'marker:text-white/60' : 'marker:text-fresh-sky-400'
      )}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className={cn(
        'list-decimal pl-5 mb-2 space-y-1',
        isUser ? 'marker:text-white/60' : 'marker:text-fresh-sky-400'
      )}>{children}</ol>
    ),
    li: ({ children }) => (
      <li className={cn(
        'text-sm md:text-base font-light leading-relaxed',
        isUser ? 'text-white' : 'text-calm-text'
      )}>{children}</li>
    ),
    code: ({ className, children, ...props }) => {
      const isBlock = className?.includes('language-');
      if (isBlock) {
        return (
          <code
            className={cn(
              'block bg-slate-800 text-slate-100 rounded-xl p-4 font-mono text-xs md:text-sm overflow-x-auto my-3',
              'leading-relaxed whitespace-pre'
            )}
            {...props}
          >
            {children}
          </code>
        );
      }
      return (
        <code
          className={cn(
            'px-1.5 py-0.5 rounded text-sm font-mono',
            isUser
              ? 'bg-white/15 text-white'
              : 'bg-steel-azure-50 text-steel-azure-700 border border-steel-azure-100'
          )}
          {...props}
        >
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className="overflow-hidden rounded-xl my-3">{children}</pre>
    ),
    blockquote: ({ children }) => (
      <blockquote className={cn(
        'border-l-3 pl-4 my-3 italic',
        isUser
          ? 'border-white/40 text-white/85'
          : 'border-fresh-sky-400 text-calm-muted bg-fresh-sky-50/50 py-2 pr-3 rounded-r-lg'
      )}>{children}</blockquote>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'underline underline-offset-2 transition-colors',
          isUser
            ? 'text-fresh-sky-200 hover:text-white'
            : 'text-steel-azure-600 hover:text-steel-azure-800'
        )}
      >
        {children}
      </a>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto my-3 rounded-xl border border-steel-azure-100">
        <table className="w-full text-sm border-collapse">{children}</table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className={cn(
        isUser ? 'bg-white/10' : 'bg-steel-azure-50'
      )}>{children}</thead>
    ),
    tbody: ({ children }) => (
      <tbody className="divide-y divide-steel-azure-100">{children}</tbody>
    ),
    tr: ({ children }) => (
      <tr className={cn(
        'even:bg-steel-azure-50/30',
        isUser && 'even:bg-white/5'
      )}>{children}</tr>
    ),
    th: ({ children }) => (
      <th className={cn(
        'px-3 py-2 text-left text-xs font-medium uppercase tracking-wider',
        isUser ? 'text-white/80' : 'text-steel-azure-600'
      )}>{children}</th>
    ),
    td: ({ children }) => (
      <td className={cn(
        'px-3 py-2 text-sm font-light',
        isUser ? 'text-white' : 'text-calm-text'
      )}>{children}</td>
    ),
    hr: () => (
      <hr className={cn(
        'my-4 border-t',
        isUser ? 'border-white/20' : 'border-steel-azure-100'
      )} />
    ),
  };

  return (
    <div className="chat-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
